import { useState, useEffect } from 'react'
import AppHeader from '@/components/layout/AppHeader'
import Button from '@/components/common/Button'
import { api } from '@/utils/auth'

// ── Score helpers ─────────────────────────────────────────────────────────────
function scoreLabel(score) {
  if (!score) return { label: '—', color: 'text-slate-500', bg: 'bg-slate-100', bar: 'bg-slate-300' }
  if (score >= 800) return { label: 'Exceptional', color: 'text-emerald-700', bg: 'bg-emerald-50', bar: 'bg-emerald-500' }
  if (score >= 740) return { label: 'Very Good',   color: 'text-green-700',   bg: 'bg-green-50',   bar: 'bg-green-500' }
  if (score >= 670) return { label: 'Good',         color: 'text-lime-700',   bg: 'bg-lime-50',    bar: 'bg-lime-500' }
  if (score >= 580) return { label: 'Fair',         color: 'text-amber-700',  bg: 'bg-amber-50',   bar: 'bg-amber-500' }
  return                    { label: 'Poor',         color: 'text-red-700',    bg: 'bg-red-50',     bar: 'bg-red-500' }
}

function scorePct(score) {
  if (!score) return 0
  return Math.round(((score - 300) / 550) * 100)
}

// ── Bureau card ───────────────────────────────────────────────────────────────
function BureauCard({ name, score, phone, color }) {
  const s = scoreLabel(score)
  return (
    <div className={`rounded-2xl p-4 ${s.bg} border border-white shadow-sm`}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">{name}</span>
        <span className="text-sm text-slate-600">{phone}</span>
      </div>
      <div className="flex items-end gap-2">
        <span className={`text-3xl font-bold ${score ? s.color : 'text-slate-300'}`}>
          {score ?? '—'}
        </span>
        {score && <span className={`text-sm font-medium mb-1 ${s.color}`}>{s.label}</span>}
      </div>
      <div className="mt-3 h-2 bg-white/60 rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full transition-all duration-700 ${s.bar}`}
          style={{ width: `${scorePct(score)}%` }}
        />
      </div>
      <div className="flex justify-between mt-1">
        <span className="text-xs text-slate-600">300</span>
        <span className="text-xs text-slate-600">850</span>
      </div>
    </div>
  )
}

// ── Manual entry tab ──────────────────────────────────────────────────────────
function ManualTab({ onSaved }) {
  const [form, setForm] = useState({ equifax: '', experian: '', transunion: '', notes: '' })
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }))

  async function handleSubmit(e) {
    e.preventDefault()
    setError('')
    const payload = {
      equifax:    form.equifax    ? parseInt(form.equifax)    : null,
      experian:   form.experian   ? parseInt(form.experian)   : null,
      transunion: form.transunion ? parseInt(form.transunion) : null,
      notes:      form.notes,
      source:     'manual',
    }
    const filled = [payload.equifax, payload.experian, payload.transunion].filter(Boolean)
    if (filled.length === 0) return setError('Enter at least one score.')
    if (filled.some(s => s < 300 || s > 850)) return setError('Scores must be between 300 and 850.')

    setSaving(true)
    try {
      const res = await api('/api/data/credit', { method: 'POST', body: JSON.stringify(payload) })
      const data = await res.json()
      if (!res.ok) throw new Error(data.error || 'Save failed')
      setForm({ equifax: '', experian: '', transunion: '', notes: '' })
      onSaved(data)
    } catch (err) {
      setError(err.message)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="space-y-5">
      {/* How to get free scores */}
      <div className="card p-4 space-y-3">
        <p className="text-sm font-semibold text-slate-700">📋 How to get your free scores</p>
        <div className="space-y-2">
          {[
            { name: 'Credit Karma', desc: 'Free, updates weekly — Equifax & TransUnion', url: 'https://www.creditkarma.com', tag: 'Recommended' },
            { name: 'Experian Free', desc: 'Free Experian score + monitoring', url: 'https://www.experian.com/consumer-products/free-credit-report.html', tag: null },
            { name: 'AnnualCreditReport.com', desc: 'Free official reports from all 3 bureaus (federally mandated)', url: 'https://www.annualcreditreport.com', tag: null },
          ].map(s => (
            <a key={s.name} href={s.url} target="_blank" rel="noreferrer"
              className="flex items-center justify-between p-3 rounded-xl bg-slate-50 hover:bg-brand-100 transition-colors">
              <div>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-slate-800">{s.name}</span>
                  {s.tag && <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium">{s.tag}</span>}
                </div>
                <p className="text-sm text-slate-500 mt-0.5">{s.desc}</p>
              </div>
              <span className="text-brand-500 text-lg">→</span>
            </a>
          ))}
        </div>
      </div>

      {/* Entry form */}
      <form onSubmit={handleSubmit} className="card p-4 space-y-4">
        <p className="text-sm font-semibold text-slate-700">✏️ Enter your scores</p>
        <div className="grid grid-cols-3 gap-3">
          {[
            { key: 'equifax',    label: 'Equifax',    phone: '1-800-349-9960' },
            { key: 'experian',   label: 'Experian',   phone: '1-888-397-3742' },
            { key: 'transunion', label: 'TransUnion', phone: '1-888-909-8872' },
          ].map(b => (
            <div key={b.key}>
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide block mb-1">{b.label}</label>
              <input
                type="number" min="300" max="850" placeholder="—"
                value={form[b.key]}
                onChange={e => set(b.key, e.target.value)}
                className="w-full border border-slate-200 rounded-xl px-3 py-2 text-center text-lg font-bold text-slate-800 focus:outline-none focus:ring-2 focus:ring-brand-500"
              />
            </div>
          ))}
        </div>
        <div>
          <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide block mb-1">Notes (optional)</label>
          <input
            type="text" placeholder="e.g. From Credit Karma, March 2026"
            value={form.notes}
            onChange={e => set('notes', e.target.value)}
            className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-brand-500"
          />
        </div>
        {error && <p className="text-sm text-red-600 bg-red-50 rounded-xl px-3 py-2">{error}</p>}
        <Button type="submit" disabled={saving} className="w-full">
          {saving ? 'Saving…' : 'Save Scores'}
        </Button>
      </form>
    </div>
  )
}

// ── Credit health tips ────────────────────────────────────────────────────────
function CreditTips() {
  const tips = [
    { heading: 'Joint accounts affect both parties', body: 'Any joint credit card or loan opened during the marriage appears on both credit reports. Late payments or high balances hurt both scores regardless of who is responsible under a divorce decree.' },
    { heading: 'Set up autopay now', body: 'Missed payments are the single biggest score damage during litigation. Legal stress, moving costs, and attorney fees make it easy to forget due dates — autopay is the safest protection.' },
    { heading: 'Refinance joint debts as soon as possible', body: 'Until a joint mortgage or car loan is refinanced into one name, both parties remain liable. A co-parent\'s default can tank your score even after a divorce is finalized.' },
    { heading: 'Opening new accounts lowers your score short-term', body: 'Each hard inquiry (new credit application) drops your score 5–10 points temporarily. Avoid applying for new credit lines in the months before or during custody hearings.' },
    { heading: 'Credit utilization matters as much as payment history', body: 'Keeping balances below 30% of your credit limit has a large positive impact. Closing old accounts in a divorce can suddenly raise your utilization ratio — plan carefully.' },
    { heading: 'Your score affects housing stability', body: 'Landlords run credit checks for rentals. A score below 620 can limit housing options at exactly the time you need stability for custody evaluations. Monitoring regularly lets you catch and dispute errors fast.' },
  ]

  return (
    <div className="bg-brand-50 border border-brand-100 rounded-2xl p-4 space-y-4">
      <p className="text-sm font-semibold text-brand-700">What affects your score — especially during divorce or custody</p>
      <div className="space-y-3">
        {tips.map((tip, i) => (
          <div key={i} className="flex gap-3">
            <span className="text-brand-500 font-bold text-sm mt-0.5 flex-shrink-0">•</span>
            <div>
              <p className="text-sm font-semibold text-slate-800">{tip.heading}</p>
              <p className="text-sm text-slate-600 mt-0.5">{tip.body}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

// ── Edit modal ────────────────────────────────────────────────────────────────
function EditModal({ entry, onClose, onUpdated }) {
  const [form, setForm] = useState({
    equifax:    entry.equifax    != null ? String(entry.equifax)    : '',
    experian:   entry.experian   != null ? String(entry.experian)   : '',
    transunion: entry.transunion != null ? String(entry.transunion) : '',
    notes:  entry.notes  || '',
    source: entry.source || 'manual',
  })
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }))

  async function handleSave(e) {
    e.preventDefault()
    setError('')
    const payload = {
      equifax:    form.equifax    ? parseInt(form.equifax)    : null,
      experian:   form.experian   ? parseInt(form.experian)   : null,
      transunion: form.transunion ? parseInt(form.transunion) : null,
      notes:  form.notes,
      source: form.source,
    }
    const filled = [payload.equifax, payload.experian, payload.transunion].filter(s => s != null)
    if (filled.length === 0) return setError('Enter at least one score.')
    if (filled.some(s => s < 300 || s > 850)) return setError('Scores must be between 300 and 850.')

    setSaving(true)
    try {
      const res = await api(`/api/data/credit?id=${entry.id}`, {
        method: 'PUT',
        body: JSON.stringify(payload),
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.error || 'Update failed')
      onUpdated({ ...entry, ...payload, average: data.average })
    } catch (err) {
      setError(err.message)
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40" onClick={onClose}>
      <div
        className="w-full max-w-lg bg-white rounded-t-2xl p-5 space-y-4 pb-8"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between">
          <p className="text-base font-semibold text-slate-800">Edit Score Entry</p>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 text-xl leading-none tap-target">✕</button>
        </div>
        <form onSubmit={handleSave} className="space-y-4">
          <div className="grid grid-cols-3 gap-3">
            {[
              { key: 'equifax',    label: 'Equifax' },
              { key: 'experian',   label: 'Experian' },
              { key: 'transunion', label: 'TransUnion' },
            ].map(b => (
              <div key={b.key}>
                <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide block mb-1">{b.label}</label>
                <input
                  type="number" min="300" max="850" placeholder="—"
                  value={form[b.key]}
                  onChange={e => set(b.key, e.target.value)}
                  className="w-full border border-slate-200 rounded-xl px-3 py-2 text-center text-lg font-bold text-slate-800 focus:outline-none focus:ring-2 focus:ring-brand-500"
                />
              </div>
            ))}
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide block mb-1">Notes (optional)</label>
            <input
              type="text" placeholder="e.g. From Credit Karma, March 2026"
              value={form.notes}
              onChange={e => set('notes', e.target.value)}
              className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-brand-500"
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide block mb-1">Source</label>
            <input
              type="text" placeholder="manual"
              value={form.source}
              onChange={e => set('source', e.target.value)}
              className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-brand-500"
            />
          </div>
          {error && <p className="text-sm text-red-600 bg-red-50 rounded-xl px-3 py-2">{error}</p>}
          <div className="flex gap-3">
            <Button type="button" variant="outline" className="flex-1" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={saving} className="flex-1">
              {saving ? 'Saving…' : 'Save Changes'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}

// ── History row ───────────────────────────────────────────────────────────────
function HistoryRow({ entry, prev, onEdit }) {
  const change = prev ? entry.average - prev.average : null
  const s = scoreLabel(entry.average)
  const date = new Date(entry.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
  return (
    <div className="flex items-center justify-between py-3 border-b border-slate-100 last:border-0">
      <div>
        <div className="flex items-center gap-2">
          <span className={`text-lg font-bold ${s.color}`}>{entry.average}</span>
          {change !== null && change !== 0 && (
            <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${change > 0 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
              {change > 0 ? `+${change}` : change}
            </span>
          )}
        </div>
        <p className="text-sm text-slate-600 mt-0.5">{date}{entry.notes ? ` · ${entry.notes}` : ''}</p>
      </div>
      <div className="flex items-center gap-3">
        <div className="text-right text-xs text-slate-600 space-y-0.5">
          {entry.equifax    && <div>EQ {entry.equifax}</div>}
          {entry.experian   && <div>EX {entry.experian}</div>}
          {entry.transunion && <div>TU {entry.transunion}</div>}
        </div>
        <button
          onClick={() => onEdit(entry)}
          className="tap-target flex items-center justify-center w-8 h-8 rounded-full hover:bg-slate-100 text-slate-400 hover:text-brand-600 transition-colors"
          aria-label="Edit score entry"
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
            <path d="M2.695 14.763l-1.262 3.154a.5.5 0 00.65.65l3.155-1.262a4 4 0 001.343-.885L17.5 5.5a2.121 2.121 0 00-3-3L3.58 13.42a4 4 0 00-.885 1.343z" />
          </svg>
        </button>
      </div>
    </div>
  )
}

// ── Main screen ───────────────────────────────────────────────────────────────
export default function CreditScore() {
  const [scores, setScores] = useState([])
  const [loading, setLoading] = useState(true)
  const [editingEntry, setEditingEntry] = useState(null)

  const latest = scores[0] || null
  const s = scoreLabel(latest?.average)

  useEffect(() => { loadScores() }, [])

  async function loadScores() {
    try {
      const res = await api('/api/data/credit')
      const data = await res.json()
      setScores(data.scores || [])
    } catch (_) {}
    setLoading(false)
  }

  function handleSaved(data) {
    loadScores()
  }

  function handleUpdated(updated) {
    setScores(prev => prev.map(e => e.id === updated.id ? updated : e))
    setEditingEntry(null)
  }

  return (
    <div className="flex flex-col h-full bg-white">
      {editingEntry && (
        <EditModal
          entry={editingEntry}
          onClose={() => setEditingEntry(null)}
          onUpdated={handleUpdated}
        />
      )}
      <AppHeader title="Credit Score" showBack />

      {/* Hero score display */}
      <div className={`brand-gradient px-6 py-5`}>
        <div className="text-center">
          {loading ? (
            <div className="text-4xl font-bold text-white/50">…</div>
          ) : latest ? (
            <>
              <div className="text-5xl font-bold text-white mb-1">{latest.average}</div>
              <div className={`inline-block px-3 py-1 rounded-full text-sm font-semibold bg-white/20 text-white mb-3`}>
                {s.label}
              </div>
              <div className="flex justify-center gap-4 text-xs text-white/80">
                {latest.equifax    && <span>EQ {latest.equifax}</span>}
                {latest.experian   && <span>EX {latest.experian}</span>}
                {latest.transunion && <span>TU {latest.transunion}</span>}
              </div>
              {scores.length > 1 && (() => {
                const change = latest.average - scores[1].average
                return change !== 0 ? (
                  <p className={`text-xs mt-2 font-semibold ${change > 0 ? 'text-green-300' : 'text-red-300'}`}>
                    {change > 0 ? `▲ +${change}` : `▼ ${change}`} since last entry
                  </p>
                ) : <p className="text-xs mt-2 text-white/60">No change since last entry</p>
              })()}
            </>
          ) : (
            <>
              <div className="text-4xl font-bold text-white/40 mb-1">—</div>
              <p className="text-sm text-white/70">No scores recorded yet</p>
            </>
          )}
        </div>
      </div>

      <div className="screen no-scrollbar px-4 py-4 space-y-4">
        {/* Bureau breakdown (latest) */}
        {latest && (
          <div className="grid grid-cols-3 gap-2">
            <BureauCard name="Equifax"    score={latest.equifax}    phone="800-349-9960" />
            <BureauCard name="Experian"   score={latest.experian}   phone="888-397-3742" />
            <BureauCard name="TransUnion" score={latest.transunion} phone="888-909-8872" />
          </div>
        )}

        <ManualTab onSaved={handleSaved} />

        <CreditTips />

        {/* Score history */}
        {scores.length > 0 && (
          <div className="card p-4">
            <p className="text-sm font-semibold text-slate-700 mb-2">📈 Score history</p>
            {scores.map((entry, i) => (
              <HistoryRow key={entry.id} entry={entry} prev={scores[i + 1] || null} onEdit={setEditingEntry} />
            ))}
          </div>
        )}

        {/* Disclaimer */}
        <div className="disclaimer">
          ⚠️ Credit scores are for personal awareness only. GoodGuardian does not share
          your scores with courts, attorneys, or third parties. Scores may affect housing
          and financial stability relevant to custody proceedings.
        </div>
      </div>
    </div>
  )
}
